import { toast } from "react-toastify";
import { BUILD_VERSION } from "../generated/buildVersion";

const VERSION_URL = `${process.env.PUBLIC_URL || ""}/version.json`;
const POLL_INTERVAL_MS = 2 * 60 * 1000;
const RELOAD_GUARD_KEY = "AppVersionReloadedFor";

let started = false;
let checking = false;
let promptShown = false;

const fetchLatestVersion = async () => {
  const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" },
  });

  if (!response.ok) {
    return "";
  }

  const payload = await response.json();
  return payload && payload.buildVersion ? String(payload.buildVersion) : "";
};

// Reloading while someone is filling a transfer form would throw their work
// away, so only reload silently when nothing is in progress.
const hasWorkInProgress = () => {
  const active = document.activeElement;
  if (
    active &&
    (active.isContentEditable ||
      ["INPUT", "TEXTAREA", "SELECT"].includes(active.tagName))
  ) {
    return true;
  }

  return Array.from(document.querySelectorAll("form input, form textarea")).some(
    (field) =>
      !field.disabled &&
      !["hidden", "submit", "button", "checkbox", "radio"].includes(field.type) &&
      field.value
  );
};

const reload = (latestVersion) => {
  // If the reload did not actually pick up the new build (stale CDN, proxy),
  // stop instead of looping the customer through endless refreshes.
  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === latestVersion) {
    return false;
  }

  sessionStorage.setItem(RELOAD_GUARD_KEY, latestVersion);
  window.location.reload();
  return true;
};

const promptToReload = (latestVersion) => {
  if (promptShown) {
    return;
  }

  promptShown = true;
  toast.info("A new version is available. Click here to update.", {
    autoClose: false,
    closeOnClick: true,
    draggable: false,
    onClick: () => {
      sessionStorage.setItem(RELOAD_GUARD_KEY, latestVersion);
      window.location.reload();
    },
  });
};

const checkForUpdate = async () => {
  if (checking) {
    return;
  }

  checking = true;

  try {
    const latestVersion = await fetchLatestVersion();

    if (!latestVersion || latestVersion === BUILD_VERSION) {
      return;
    }

    if (document.visibilityState === "hidden" || !hasWorkInProgress()) {
      if (reload(latestVersion)) {
        return;
      }
    }

    promptToReload(latestVersion);
  } catch (error) {
    // Offline or a blip - the next check will pick the update up.
  } finally {
    checking = false;
  }
};

export const startVersionWatcher = () => {
  if (started || process.env.NODE_ENV !== "production") {
    return;
  }

  started = true;

  setInterval(checkForUpdate, POLL_INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      checkForUpdate();
    }
  });

  checkForUpdate();
};
